/**
 * Canvas state <-> canonical v2 graph JSON for the Agent Composer.
 * Owns React Flow nodes/edges, node creation, connection rules
 * (condition edges get true/false labels), auto-layout, and serialization.
 */
import { useCallback, useRef } from 'react';
import {
  useNodesState,
  useEdgesState,
  addEdge,
  MarkerType,
  type Node,
  type Edge,
  type Connection,
} from '@xyflow/react';
import dagre from '@dagrejs/dagre';
import type {
  ComposerNodeData,
  ComposerNodeType,
  WorkflowGraphV2,
} from '../../types/composer';

export type ComposerNode = Node<ComposerNodeData>;
export type ComposerEdge = Edge;

const NODE_TYPES: ComposerNodeType[] = ['agent', 'condition', 'wait', 'parallel', 'merge'];

const DEFAULT_LABELS: Record<ComposerNodeType, string> = {
  agent: 'Agent',
  condition: 'Condition',
  wait: 'Wait',
  parallel: 'Parallel',
  merge: 'Merge',
};

const DEFAULT_CONFIGS: Record<ComposerNodeType, Record<string, unknown>> = {
  agent: { agentDefinitionId: '', message: '{{input}}' },
  condition: { condition: '' },
  wait: { waitType: 'Time', waitDurationSeconds: 30 },
  parallel: {},
  merge: {},
};

/** Numeric legacy enum -> canvas node type. */
const LEGACY_TYPE_MAP: Record<number, ComposerNodeType> = {
  0: 'agent',
  1: 'condition',
  2: 'wait',
  3: 'parallel',
  4: 'merge',
};

const edgeDefaults = {
  type: 'smoothstep' as const,
  markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16 },
};

/** Generates a short readable node id unique within the graph (e.g. agent-2). */
const nextNodeId = (type: ComposerNodeType, nodes: ComposerNode[]): string => {
  let i = 1;
  while (nodes.some((n) => n.id === `${type}-${i}`)) i += 1;
  return `${type}-${i}`;
};

/** True when the agent message is still the create-time default (or empty). */
export const isDefaultAgentMessage = (message: unknown): boolean => {
  const msg = typeof message === 'string' ? message.trim() : '';
  return !msg || msg === '{{input}}';
};

/**
 * Walks inbound edges from `fromNodeId` and returns the nearest upstream agent id.
 * Used to auto-wire `{{output_<id>}}` when connecting through condition/wait/etc.
 */
export const findUpstreamAgentId = (
  fromNodeId: string,
  nodes: ComposerNode[],
  edges: ComposerEdge[],
): string | undefined => {
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const visited = new Set<string>();
  const queue = [fromNodeId];

  while (queue.length > 0) {
    const id = queue.shift()!;
    if (visited.has(id)) continue;
    visited.add(id);

    const node = nodeById.get(id);
    if (node?.type === 'agent') return id;

    for (const edge of edges) {
      if (edge.target === id && edge.source) {
        queue.push(edge.source);
      }
    }
  }

  return undefined;
};

/** Serializes canvas state to the canonical v2 graph JSON string. */
export const serializeGraph = (nodes: ComposerNode[], edges: ComposerEdge[]): string => {
  const graph: WorkflowGraphV2 = {
    version: 2,
    nodes: nodes.map((n) => ({
      id: n.id,
      type: (n.type ?? 'agent') as ComposerNodeType,
      label: n.data.label,
      isStart: n.data.isStart || undefined,
      position: { x: Math.round(n.position.x), y: Math.round(n.position.y) },
      data: n.data.config,
    })),
    edges: edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      label: typeof e.label === 'string' && e.label ? e.label : null,
    })),
  };
  return JSON.stringify(graph, null, 2);
};

/**
 * Parses workflow JSON into canvas nodes/edges. Tolerates both the v2 shape and
 * the legacy engine shape (PascalCase, numeric Type, SourceId/TargetId).
 */
export const parseGraph = (workflowJson: string): { nodes: ComposerNode[]; edges: ComposerEdge[] } => {
  let raw: Record<string, unknown>;
  try {
    raw = JSON.parse(workflowJson) as Record<string, unknown>;
  } catch {
    return { nodes: [], edges: [] };
  }

  const rawNodes = (raw.nodes ?? raw.Nodes ?? []) as Array<Record<string, unknown>>;
  const rawEdges = (raw.edges ?? raw.Edges ?? []) as Array<Record<string, unknown>>;

  const nodes: ComposerNode[] = rawNodes.map((rn, index) => {
    const rawType = rn.type ?? rn.Type;
    let type: ComposerNodeType = 'agent';
    if (typeof rawType === 'number') {
      type = LEGACY_TYPE_MAP[rawType] ?? 'agent';
    } else if (typeof rawType === 'string') {
      const lower = rawType.toLowerCase();
      type = (NODE_TYPES as string[]).includes(lower) ? (lower as ComposerNodeType) : 'agent';
    }

    let config: Record<string, unknown> = {};
    if (rn.data && typeof rn.data === 'object') {
      config = rn.data as Record<string, unknown>;
    } else {
      const legacyConfig = rn.configurationJson ?? rn.ConfigurationJson;
      if (typeof legacyConfig === 'string' && legacyConfig.trim()) {
        try {
          config = JSON.parse(legacyConfig) as Record<string, unknown>;
        } catch {
          config = {};
        }
      }
    }

    const position = (rn.position ?? rn.Position) as { x?: number; y?: number } | undefined;
    const id = String(rn.id ?? rn.Id ?? `node-${index + 1}`);

    return {
      id,
      type,
      position: {
        x: typeof position?.x === 'number' ? position.x : 80 + index * 280,
        y: typeof position?.y === 'number' ? position.y : 120,
      },
      data: {
        label: String(rn.label ?? rn.Label ?? DEFAULT_LABELS[type]),
        isStart: Boolean(rn.isStart ?? rn.IsStart ?? false),
        config,
      },
    } satisfies ComposerNode;
  });

  const edges: ComposerEdge[] = rawEdges.map((re, index) => {
    const source = String(re.source ?? re.sourceId ?? re.SourceId ?? '');
    const target = String(re.target ?? re.targetId ?? re.TargetId ?? '');
    const label = re.label ?? re.Label;
    return {
      ...edgeDefaults,
      id: String(re.id ?? re.Id ?? `e-${index + 1}`),
      source,
      target,
      label: typeof label === 'string' && label ? label : undefined,
    } satisfies ComposerEdge;
  });

  // If nothing is flagged as start, promote the single node without inbound edges.
  if (nodes.length > 0 && !nodes.some((n) => n.data.isStart)) {
    const targets = new Set(edges.map((e) => e.target));
    const candidates = nodes.filter((n) => !targets.has(n.id));
    if (candidates.length === 1) {
      candidates[0].data = { ...candidates[0].data, isStart: true };
    }
  }

  return { nodes, edges };
};

/** Dagre left-to-right auto layout. */
export const layoutGraph = (nodes: ComposerNode[], edges: ComposerEdge[]): ComposerNode[] => {
  if (nodes.length === 0) return nodes;

  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: 'LR', ranksep: 110, nodesep: 60 });
  g.setDefaultEdgeLabel(() => ({}));

  nodes.forEach((n) => g.setNode(n.id, { width: 220, height: 96 }));
  edges.forEach((e) => g.setEdge(e.source, e.target));

  dagre.layout(g);

  return nodes.map((n) => {
    const pos = g.node(n.id);
    return { ...n, position: { x: pos.x - 110, y: pos.y - 48 } };
  });
};

/**
 * Composer graph state hook.
 */
export const useComposerGraph = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState<ComposerNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<ComposerEdge>([]);
  const edgeCounter = useRef(1);

  /** Adds a node of the given type at a canvas position. First node becomes the start node. */
  const addNode = useCallback(
    (type: ComposerNodeType, position: { x: number; y: number }): string => {
      let createdId = '';
      setNodes((current) => {
        const id = nextNodeId(type, current);
        createdId = id;
        const node: ComposerNode = {
          id,
          type,
          position,
          data: {
            label: `${DEFAULT_LABELS[type]} ${id.split('-')[1]}`,
            isStart: current.length === 0,
            config: { ...DEFAULT_CONFIGS[type] },
          },
        };
        return [...current, node];
      });
      return createdId;
    },
    [setNodes],
  );

  /** Connects two nodes; edges leaving a condition node get true/false labels automatically. */
  const onConnect = useCallback(
    (connection: Connection) => {
      if (!connection.source || !connection.target) return;

      const sourceNode = nodes.find((n) => n.id === connection.source);
      let label: string | undefined;
      if (sourceNode?.type === 'condition') {
        const existingLabels = edges
          .filter((e) => e.source === connection.source)
          .map((e) => (typeof e.label === 'string' ? e.label.toLowerCase() : ''));
        label = existingLabels.includes('true') ? 'false' : 'true';
      }

      edgeCounter.current += 1;
      const newEdge: ComposerEdge = {
        ...connection,
        source: connection.source,
        target: connection.target,
        ...edgeDefaults,
        id: `e-${Date.now()}-${edgeCounter.current}`,
        label,
      };

      setEdges((currentEdges) => addEdge(newEdge, currentEdges));

      // Auto-wire downstream agent intake: default {{input}} → {{output_<upstreamAgent>}}
      // so chained agents receive prior results instead of re-running the same prompt.
      const targetNode = nodes.find((n) => n.id === connection.target);
      if (targetNode?.type !== 'agent') return;
      if (!isDefaultAgentMessage(targetNode.data.config?.message)) return;

      const edgesWithNew = [...edges, newEdge];
      const upstreamAgentId =
        sourceNode?.type === 'agent'
          ? sourceNode.id
          : findUpstreamAgentId(connection.source, nodes, edgesWithNew);

      if (!upstreamAgentId || upstreamAgentId === connection.target) return;

      setNodes((current) =>
        current.map((n) => {
          if (n.id !== connection.target || n.type !== 'agent') return n;
          if (!isDefaultAgentMessage(n.data.config?.message)) return n;
          return {
            ...n,
            data: {
              ...n.data,
              config: {
                ...n.data.config,
                message: `{{output_${upstreamAgentId}}}`,
              },
            },
          };
        }),
      );
    },
    [nodes, edges, setEdges, setNodes],
  );

  /** Updates a node's data (label, config, isStart). */
  const updateNodeData = useCallback(
    (nodeId: string, patch: Partial<ComposerNodeData>) => {
      setNodes((current) =>
        current.map((n) => {
          if (n.id !== nodeId) {
            // Only one start node at a time.
            if (patch.isStart && n.data.isStart) {
              return { ...n, data: { ...n.data, isStart: false } };
            }
            return n;
          }
          return { ...n, data: { ...n.data, ...patch } };
        }),
      );
    },
    [setNodes],
  );

  /** Updates an edge's label (condition branch selector). */
  const updateEdgeLabel = useCallback(
    (edgeId: string, label: string | undefined) => {
      setEdges((current) => current.map((e) => (e.id === edgeId ? { ...e, label } : e)));
    },
    [setEdges],
  );

  const removeNode = useCallback(
    (nodeId: string) => {
      setNodes((current) => current.filter((n) => n.id !== nodeId));
      setEdges((current) => current.filter((e) => e.source !== nodeId && e.target !== nodeId));
    },
    [setNodes, setEdges],
  );

  const loadFromJson = useCallback(
    (workflowJson: string) => {
      const parsed = parseGraph(workflowJson);
      setNodes(parsed.nodes);
      setEdges(parsed.edges);
    },
    [setNodes, setEdges],
  );

  const applyAutoLayout = useCallback(() => {
    setNodes((current) => layoutGraph(current, edges));
  }, [edges, setNodes]);

  const toJson = useCallback(() => serializeGraph(nodes, edges), [nodes, edges]);

  return {
    nodes,
    edges,
    setNodes,
    setEdges,
    onNodesChange,
    onEdgesChange,
    onConnect,
    addNode,
    updateNodeData,
    updateEdgeLabel,
    removeNode,
    loadFromJson,
    applyAutoLayout,
    toJson,
  };
};
